import { FLOW_ALTITUDES, type Diagram, type DiagramNode, type FlowAltitude, type View } from '@civil/schema';

/**
 * Altitude (docs/altitude.md): the motion *up*. A flow is re-described at a coarser
 * grain — Story above Product above Mechanism — and a node at one altitude covers
 * nodes of the one below, many-to-many. This is not the canvas's `Altitude` (a level of
 * the descent stack, the motion *in*); a door is containment, a cover is not.
 *
 * Moving between altitudes carries the selection across as a footprint: what the
 * selected nodes are, said at the other altitude. Down from Story it is what the moments
 * cover; up to Story it is the moments that cover the selection; between Product and
 * Mechanism, which say no covers, it is the nodes that share a part.
 */

export interface LoadedDiagrams {
  view: View;
  diagrams?: Record<string, { path: string; doc: Diagram }>;
}

export const ALTITUDE_NAMES: Record<FlowAltitude, string> = { story: 'Story', product: 'Product', mechanism: 'Mechanism' };

/** The altitudes a view has, highest first, each with the diagram it opens at. */
export function altitudesOf(loaded: LoadedDiagrams | undefined): { altitude: FlowAltitude; entry: string }[] {
  const named = loaded?.view.spec.altitudes;
  if (!loaded?.diagrams || !named) return [];
  return FLOW_ALTITUDES.flatMap((altitude) => {
    const entry = named[altitude];
    return entry && loaded.diagrams![entry]?.doc.spec.altitude === altitude ? [{ altitude, entry }] : [];
  });
}

/** The altitude a diagram is at, when its view has altitudes. */
export function altitudeOf(loaded: LoadedDiagrams | undefined, diagram: string): FlowAltitude | undefined {
  return loaded?.diagrams?.[diagram]?.doc.spec.altitude;
}

/** A node somewhere in the view, with the diagram that holds it. */
export interface Placed {
  diagram: string;
  diagramName: string;
  node: DiagramNode;
}

const inDiagrams = (loaded: LoadedDiagrams, altitude: FlowAltitude): [string, Diagram][] =>
  Object.entries(loaded.diagrams ?? {})
    .filter(([, d]) => d.doc.spec.altitude === altitude)
    .map(([id, d]) => [id, d.doc]);

const place = (id: string, doc: Diagram, node: DiagramNode): Placed => ({ diagram: id, diagramName: doc.metadata.name ?? id, node });

/** Selected nodes and, for a door, the nodes of the diagram it opens: what a selection stands for. */
function standsFor(loaded: LoadedDiagrams, diagram: string, ids: readonly string[]): Placed[] {
  const doc = loaded.diagrams?.[diagram]?.doc;
  if (!doc) return [];
  const out: Placed[] = [];
  for (const node of doc.spec.nodes.filter((n) => ids.includes(n.id))) {
    out.push(place(diagram, doc, node));
    const inside = node.ref ? loaded.diagrams?.[node.ref]?.doc : undefined;
    if (inside && inside.spec.altitude === doc.spec.altitude) for (const n of inside.spec.nodes) out.push(place(node.ref!, inside, n));
  }
  return out;
}

const dedupe = (placed: Placed[]): Placed[] => {
  const seen = new Set<string>();
  return placed.filter((p) => {
    const key = `${p.diagram}:${p.node.id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/** One step down: what the nodes cover, else the nodes below that share a part with them. */
function stepDown(loaded: LoadedDiagrams, from: Placed[], to: FlowAltitude): Placed[] {
  const below = inDiagrams(loaded, to);
  const covered = from.flatMap((p) =>
    p.node.covers.flatMap((c) => {
      const doc = loaded.diagrams?.[c.diagram]?.doc;
      return doc && doc.spec.altitude === to ? doc.spec.nodes.filter((n) => c.nodes.includes(n.id)).map((n) => place(c.diagram, doc, n)) : [];
    }),
  );
  if (from.some((p) => p.node.covers.length)) return dedupe(covered);
  return dedupe(sharingParts(below, from));
}

/** One step up: the nodes whose covers name any of these, else those that share a part. */
function stepUp(loaded: LoadedDiagrams, from: Placed[], to: FlowAltitude): Placed[] {
  const above = inDiagrams(loaded, to);
  const keys = new Set(from.map((p) => `${p.diagram}:${p.node.id}`));
  const covering = above.flatMap(([id, doc]) =>
    doc.spec.nodes.filter((n) => n.covers.some((c) => c.nodes.some((x) => keys.has(`${c.diagram}:${x}`)))).map((n) => place(id, doc, n)),
  );
  if (above.some(([, doc]) => doc.spec.nodes.some((n) => n.covers.length))) return dedupe(covering);
  return dedupe(sharingParts(above, from));
}

function sharingParts(diagrams: [string, Diagram][], from: Placed[]): Placed[] {
  const parts = new Set(from.flatMap((p) => p.node.realizedBy));
  if (!parts.size) return [];
  return diagrams.flatMap(([id, doc]) => doc.spec.nodes.filter((n) => n.realizedBy.some((r) => parts.has(r))).map((n) => place(id, doc, n)));
}

/**
 * What `ids`, selected in `diagram`, are at altitude `to`: every node there, and the
 * ones to select on that altitude's entry — the nodes themselves when the entry holds
 * them, else the entry's doors into the diagrams that do.
 */
export function footprint(
  loaded: LoadedDiagrams,
  diagram: string,
  ids: readonly string[],
  to: FlowAltitude,
): { all: Placed[]; onEntry: string[] } {
  const from = altitudeOf(loaded, diagram);
  const entry = loaded.view.spec.altitudes?.[to];
  if (!from || !entry || from === to) return { all: [], onEntry: [] };
  const step = FLOW_ALTITUDES.indexOf(to) > FLOW_ALTITUDES.indexOf(from) ? 1 : -1;
  let at = standsFor(loaded, diagram, ids);
  for (let i = FLOW_ALTITUDES.indexOf(from) + step; at.length; i += step) {
    const altitude = FLOW_ALTITUDES[i]!;
    at = step > 0 ? stepDown(loaded, at, altitude) : stepUp(loaded, at, altitude);
    if (altitude === to) break;
  }
  const entryDoc = loaded.diagrams?.[entry]?.doc;
  const holding = new Set(at.map((p) => p.diagram));
  const onEntry = entryDoc
    ? entryDoc.spec.nodes.filter((n) => at.some((p) => p.diagram === entry && p.node.id === n.id) || (n.ref !== undefined && holding.has(n.ref))).map((n) => n.id)
    : [];
  return { all: at, onEntry };
}
